import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  User,
  Phone,
  CreditCard,
  Calendar,
  X,
  Check,
  Camera,
  Shield,
  Loader2,
  AlertTriangle,
  Heart,
  Clock
} from 'lucide-react'
import { authApi, usersApi, uploadApi, hospitalsApi } from '../../lib/api'
import { usePageTitle } from '../../hooks/usePageTitle'

const CROWD_TYPES = ['慢性病患者', '慢性病高危人群', '普通人群']
const AGE_GROUPS = ['>65', '<=65']

const PersonalInfo = () => {
  usePageTitle()
  const navigate = useNavigate()
  const [user, setUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showIdModal, setShowIdModal] = useState(false)
  const [editName, setEditName] = useState('')
  const [editIdCard, setEditIdCard] = useState('')
  const [saving, setSaving] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  // 登录守卫
  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/user/login')
    }
  }, [navigate])

  // 补充健康信息弹窗
  const [showSupplementModal, setShowSupplementModal] = useState(false)
  const [supplementData, setSupplementData] = useState({ hospital_id: '', crowd_type: '', age_group: '' })
  const [hospitals, setHospitals] = useState<{ id: string; name: string }[]>([])
  const [loadingHospitals, setLoadingHospitals] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [toast, setToast] = useState<{ msg: string; type: 'error' | 'success' } | null>(null)

  const showToast = (msg: string, type: 'error' | 'success' = 'error') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 2500)
  }

  // 检查是否需要补充信息
  const needSupplement = user && (!user.hospitalId || !user.crowdType || !user.ageGroup)

  // 刷新用户信息
  const refreshUser = async () => {
    const resp = await authApi.refreshUserInfo()
    if (resp) {
      setUser(resp)
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    refreshUser().finally(() => setLoading(false))
    loadHospitals()
  }, [])

  // 页面可见时刷新
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUser()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  // 检查是否需要补充信息，自动弹窗（仅在三个字段全空时触发一次）
  useEffect(() => {
    if (user && !user.hospitalId && !user.crowdType && !user.ageGroup) {
      setShowSupplementModal(true)
    }
  }, [user])

  // 加载机构列表
  const loadHospitals = async () => {
    setLoadingHospitals(true)
    try {
      const res: any = await hospitalsApi.list({ is_active: true })
      setHospitals((res.hospitals || res || []).map((h: any) => ({ id: h.id, name: h.name })))
    } catch {} finally {
      setLoadingHospitals(false)
    }
  }

  // 打开补充信息弹窗
  const openSupplementModal = () => {
    setSupplementData({
      hospital_id: user?.hospitalId || '',
      crowd_type: user?.crowdType || '',
      age_group: user?.ageGroup || ''
    })
    loadHospitals()
    setShowSupplementModal(true)
  }

  // 提交补充信息
  const handleSupplementSubmit = async () => {
    if (!supplementData.hospital_id) { showToast('请选择绑定机构'); return }
    if (!supplementData.crowd_type) { showToast('请选择人群类型'); return }
    if (!supplementData.age_group) { showToast('请选择年龄范围'); return }
    setSubmitting(true)
    try {
      await usersApi.updateMe({
        hospital_id: supplementData.hospital_id || undefined,
        crowd_type: supplementData.crowd_type,
        age_group: supplementData.age_group
      })
      // 无论 refreshUser 是否成功，都关闭弹窗
      setShowSupplementModal(false)
      try {
        await refreshUser()
      } catch {}
      setShowSuccess(true)
      setTimeout(() => setShowSuccess(false), 2000)
    } catch (e) {
      showToast('保存失败，请重试')
    } finally {
      setSubmitting(false)
    }
  }

  // 打开身份证填写弹窗
  const openIdModal = () => {
    setEditName('')
    setEditIdCard('')
    setShowIdModal(true)
  }

  // 提交姓名+身份证号
  const handleIdSubmit = async () => {
    if (!editName.trim()) {
      alert('姓名不能为空')
      return
    }
    if (!editIdCard.trim() || editIdCard.length !== 18) {
      alert('请填写正确的18位身份证号')
      return
    }
    setSaving(true)
    try {
      await usersApi.updateMe({ name: editName, id_card: editIdCard })
      await refreshUser()
      setShowIdModal(false)
      setShowSuccess(true)
      setTimeout(() => setShowSuccess(false), 2000)
    } catch (e) {
      alert('保存失败，请重试')
    } finally {
      setSaving(false)
    }
  }

  // 头像上传（裁剪 200x200 方形后上传）
  const [avatarUploading, setAvatarUploading] = useState(false)

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    setAvatarUploading(true)
    try {
      // 读取图片并裁剪为 200x200 方形
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
        img.onerror = reject
        img.src = url
      })

      const size = 200
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')!

      // 取中心正方形区域
      const minDim = Math.min(img.naturalWidth, img.naturalHeight)
      const sx = (img.naturalWidth - minDim) / 2
      const sy = (img.naturalHeight - minDim) / 2
      ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size)

      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas toBlob failed')), 'image/jpeg', 0.85)
      )

      // 上传文件
      const avatarUrl = await uploadApi.uploadImage(
        new File([blob], 'avatar.jpg', { type: 'image/jpeg' })
      )

      // 保存头像 URL
      await usersApi.updateMe({ avatar: avatarUrl })
      await refreshUser()
      setShowSuccess(true)
      setTimeout(() => setShowSuccess(false), 2000)
    } catch {
      alert('头像上传失败，请重试')
    } finally {
      setAvatarUploading(false)
    }
  }

  // 身份证号脱敏
  const maskIdCard = (idCard: string) => {
    if (!idCard || idCard.length < 10) return idCard || ''
    return idCard.slice(0, 6) + '********' + idCard.slice(-4)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    )
  }

  const hasIdCard = user?.idCard && user.idCard.length > 0

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* 顶部 */}
      <div className="bg-gradient-to-br from-green-600 to-green-700 px-4 pt-6 pb-8">
        <div className="flex items-center justify-center">
          <span className="text-white font-bold text-base">个人资料</span>
        </div>
        <p className="text-green-100 text-sm text-center mt-2">
          编辑和完善您的个人信息
        </p>
      </div>

      {/* 头像区域 */}
      <div className="px-4 -mt-4">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex flex-col items-center">
            <div className="relative">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
                {avatarUploading ? (
                  <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
                ) : (
                  <img src={user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.name || 'user'}`} alt={user?.name || ''} className="w-full h-full object-cover" />
                )}
              </div>
              <label className={`absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center text-white shadow-lg cursor-pointer transition-opacity ${avatarUploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}>
                <Camera className="w-4 h-4" />
                <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} disabled={avatarUploading} />
              </label>
            </div>
            <p className="text-sm text-gray-500 mt-3">点击头像更换照片</p>
          </div>
        </div>
      </div>

      {/* 个人信息表单 */}
      <div className="px-4 mt-4">
        <div className="bg-white rounded-xl shadow-md divide-y divide-gray-100">
          {/* 会员ID */}
          <div className="p-4">
            <label className="block text-sm font-medium text-gray-500 mb-2">会员ID</label>
            <div className="flex items-center">
              <Shield className="w-5 h-5 text-gray-400 mr-3" />
              <span className="text-gray-800 font-mono">{user?.memberNo || '暂无'}</span>
            </div>
          </div>

          {/* 姓名 */}
          <div className="p-4">
            <label className="block text-sm font-medium text-gray-500 mb-2">姓名</label>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <User className="w-5 h-5 text-gray-400 mr-3" />
                <span className="text-gray-800">{user?.name || ''}</span>
              </div>
            </div>
          </div>

          {/* 手机号 */}
          <div className="p-4">
            <label className="block text-sm font-medium text-gray-500 mb-2">手机号</label>
            <div className="flex items-center">
              <Phone className="w-5 h-5 text-gray-400 mr-3" />
              <span className="text-gray-800">{user?.phone || '暂无'}</span>
            </div>
          </div>

          {/* 身份证号 */}
          <div className="p-4">
            <label className="block text-sm font-medium text-gray-500 mb-2">身份证号</label>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CreditCard className="w-5 h-5 text-gray-400 mr-3" />
                {hasIdCard ? (
                  <span className="text-gray-800">{maskIdCard(user.idCard)}</span>
                ) : (
                  <span className="text-gray-400">暂未填写</span>
                )}
              </div>
              <button
                onClick={openIdModal}
                disabled={hasIdCard}
                className={`text-xs px-3 py-1 rounded-full transition-colors ${
                  hasIdCard
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'text-green-600 bg-green-50 hover:bg-green-100'
                }`}
              >
                {hasIdCard ? '不可修改' : '点击填写'}
              </button>
            </div>
          </div>

          {/* 注册时间 */}
          <div className="p-4">
            <label className="block text-sm font-medium text-gray-500 mb-2">注册时间</label>
            <div className="flex items-center">
              <Calendar className="w-5 h-5 text-gray-400 mr-3" />
              <span className="text-gray-800">{user?.createdAt ? new Date(user.createdAt).toLocaleDateString('zh-CN') : ''}</span>
            </div>
          </div>

          {/* 健康信息 */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-500">健康信息</label>
              <button
                onClick={openSupplementModal}
                className="text-xs px-3 py-1 rounded-full text-green-600 bg-green-50 hover:bg-green-100 transition-colors"
              >
                {needSupplement ? '点击补充' : '修改'}
              </button>
            </div>
            <div className="flex items-center">
              <Heart className="w-5 h-5 text-gray-400 mr-3" />
              <div className="flex flex-wrap gap-2">
                {user?.hospitalId ? (
                  <span className="px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-full flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    {hospitals.find(h => h.id === user.hospitalId)?.name || user.hospitalId}
                  </span>
                ) : null}
                {user?.crowdType ? (
                  <button
                    onClick={openSupplementModal}
                    className="px-2 py-1 bg-green-50 text-green-600 text-xs rounded-full hover:bg-green-100 transition-colors cursor-pointer"
                  >{user.crowdType}</button>
                ) : null}
                {user?.ageGroup ? (
                  <button
                    onClick={openSupplementModal}
                    className="px-2 py-1 bg-purple-50 text-purple-600 text-xs rounded-full hover:bg-purple-100 transition-colors cursor-pointer"
                  >
                    {user.ageGroup === '>65' ? '65岁以上' : '65岁及以下'}
                  </button>
                ) : null}
                {!user?.hospitalId && !user?.crowdType && !user?.ageGroup && (
                  <span className="text-gray-400 text-xs">未填写</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 提示信息 */}
      <div className="px-4 mt-6">
        <div className="bg-blue-50 rounded-xl p-4">
          <p className="text-sm text-blue-700">
            <strong>温馨提示：</strong>
          </p>
          <ul className="text-sm text-blue-600 mt-2 space-y-1">
            <li>• 身份证号填写后无法主动修改，请确保信息真实</li>
          </ul>
        </div>
      </div>

      {/* 底部版权 */}
      <div className="px-4 mt-8 text-center">
        <p className="text-xs text-gray-400">{localStorage.getItem('copyright') || '© 2026 眉山疾控'}</p>
      </div>

      {/* 身份证填写弹窗 */}
      {showIdModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 shadow-2xl overflow-hidden">
            {/* 弹窗头部 */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">完善身份信息</h3>
                <button onClick={() => setShowIdModal(false)} className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* 警告提示 */}
            <div className="mx-4 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start">
              <AlertTriangle className="w-5 h-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                为保证真实本人使用，请填写本人信息，提交后无法主动修改
              </p>
            </div>

            {/* 表单内容 */}
            <div className="p-6 space-y-4">
              {/* 姓名 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">姓名</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="请输入真实姓名"
                />
              </div>

              {/* 身份证号 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">身份证号</label>
                <input
                  type="text"
                  value={editIdCard}
                  onChange={(e) => {
                    let val = e.target.value.toUpperCase().replace(/[^\dX]/g, '').slice(0, 18)
                    setEditIdCard(val)
                  }}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="请输入18位身份证号"
                  maxLength={18}
                />
                <p className="text-xs text-gray-400 mt-1">请填写18位身份证号码</p>
              </div>

              {/* 按钮 */}
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={() => setShowIdModal(false)}
                  disabled={saving}
                  className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleIdSubmit}
                  disabled={saving}
                  className="flex-1 py-3 bg-green-700 text-white rounded-xl font-medium hover:bg-green-800 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : '确认提交'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 保存成功提示 */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl px-8 py-6 shadow-2xl">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800">保存成功</p>
                <p className="text-sm text-gray-500">您的资料已更新</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 补充健康信息弹窗（机构+疾病类型+年龄） */}
      {showSupplementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 shadow-2xl overflow-hidden">
            {/* 弹窗头部 */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">完善健康信息</h3>
                <button onClick={() => setShowSupplementModal(false)} className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* 提示信息 */}
            <div className="mx-4 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-start">
              <Heart className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">
                请完善您的健康信息，以便我们为您提供更好的服务
              </p>
            </div>

            {/* 表单内容 */}
            <div className="p-6 space-y-4">
              {/* 机构选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  绑定机构 {supplementData.hospital_id ? <span className="text-xs text-gray-400">(保存后不可修改)</span> : <span className="text-xs text-red-500">*</span>}
                </label>
                {loadingHospitals ? (
                  <div className="py-3 text-center text-gray-400">加载中...</div>
                ) : (
                  <select
                    value={supplementData.hospital_id}
                    disabled={!!supplementData.hospital_id}
                    onChange={(e) => setSupplementData({ ...supplementData, hospital_id: e.target.value })}
                    className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      supplementData.hospital_id
                        ? 'bg-gray-100 text-gray-600 cursor-not-allowed border-gray-200'
                        : 'border-gray-200 text-gray-700'
                    }`}
                  >
                    <option value="">请选择机构</option>
                    {hospitals.map(h => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                )}
                {!supplementData.hospital_id && (
                  <p className="text-xs text-gray-400 mt-1">请先选择机构（保存后不可修改）</p>
                )}
              </div>

              {/* 疾病类型 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">人群类型 <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-3 gap-2">
                  {CROWD_TYPES.map(ct => (
                    <button
                      key={ct}
                      type="button"
                      onClick={() => setSupplementData({ ...supplementData, crowd_type: ct })}
                      className={`py-2 px-3 text-sm rounded-xl border-2 transition-colors ${
                        supplementData.crowd_type === ct
                          ? 'border-green-600 bg-green-50 text-green-700'
                          : 'border-gray-200 text-gray-600 hover:border-green-400'
                      }`}
                    >
                      {ct}
                    </button>
                  ))}
                </div>
              </div>

              {/* 年龄范围 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">年龄范围 <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 gap-3">
                  {AGE_GROUPS.map(ag => (
                    <button
                      key={ag}
                      type="button"
                      onClick={() => setSupplementData({ ...supplementData, age_group: ag })}
                      className={`py-3 px-4 text-sm rounded-xl border-2 transition-colors flex items-center justify-center gap-2 ${
                        supplementData.age_group === ag
                          ? 'border-green-600 bg-green-50 text-green-700'
                          : 'border-gray-200 text-gray-600 hover:border-green-400'
                      }`}
                    >
                      <Clock className="w-4 h-4" />
                      {ag === '>65' ? '65岁以上' : '65岁及以下'}
                    </button>
                  ))}
                </div>
              </div>

              {/* 按钮 */}
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={() => setShowSupplementModal(false)}
                  disabled={submitting}
                  className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleSupplementSubmit}
                  disabled={submitting}
                  className="flex-1 py-3 bg-green-700 text-white rounded-xl font-medium hover:bg-green-800 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : '确认提交'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast 提示 */}
      {toast && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[100] px-6 py-3 rounded-xl shadow-lg text-white text-sm font-medium"
          style={{ backgroundColor: toast.type === 'error' ? '#ef4444' : '#22c55e', minWidth: 160 }}>
          {toast.msg}
        </div>
      )}
    </div>
  )
}

export default PersonalInfo